import * as Api from '@ViewModels';
import { Field, FieldLabel } from '@HoudiniUI/Field';
import { Input } from '@HoudiniUI/Input';
import { useEffect, useState } from 'react';
import { IAIPromptTemplate } from '../../../../models/AIPromptTemplate';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { useRenameAIPrompt } from '../../../../queries/AIPrompt';
import { Button } from '@HoudiniUI/Button';
import { Dialog, DialogBody, DialogFooter, DialogHeader, DialogPopup, DialogTitle } from '@HoudiniUI/Dialog';

interface IProps {
	impersonationContext?: Api.IImpersonationContext;
	/** The prompt being renamed; null keeps the dialog closed. */
	prompt: IAIPromptTemplate | null;
	onOpenChange: (open: boolean) => void;
}

export const RenamePromptDialog: React.FC<IProps> = ({ impersonationContext, prompt, onOpenChange }) => {
	const errorMessages = useErrorMessages();
	const [name, setName] = useState('');

	// Reseeded each time a different prompt opens it, so the field starts from that prompt's current name.
	useEffect(() => {
		setName(prompt?.name ?? '');
	}, [prompt]);

	const rename = useRenameAIPrompt({
		impersonationContext,
		onError: error => errorMessages.pushApiError(error),
		onSuccess: () => onOpenChange(false),
	});

	const trimmed = name.trim();
	const canSave = !!prompt && trimmed.length > 0 && trimmed !== prompt.name && !rename.isPending;

	return (
		<Dialog onOpenChange={onOpenChange} open={!!prompt}>
			<DialogPopup>
				<DialogHeader>
					<DialogTitle>Rename prompt</DialogTitle>
				</DialogHeader>
				<DialogBody>
					<Field>
						<FieldLabel htmlFor='ai-campaign-prompt-rename'>Name</FieldLabel>
						<Input id='ai-campaign-prompt-rename' onChange={event => setName(event.target.value)} value={name} />
					</Field>
				</DialogBody>
				<DialogFooter>
					<Button disabled={rename.isPending} onClick={() => onOpenChange(false)} variant='outline'>
						Cancel
					</Button>
					<Button
						disabled={!canSave}
						isLoading={rename.isPending}
						onClick={() => prompt && rename.mutate({ id: prompt.id, name: trimmed })}
					>
						Save
					</Button>
				</DialogFooter>
			</DialogPopup>
		</Dialog>
	);
};
